import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import {
  AuthenticationDetails,
  CognitoUser,
  type IAuthenticationCallback,
} from "amazon-cognito-identity-js";
import { getPool } from "./cognito.js";

export type AuthStatus =
  | "loading"
  | "signedOut"
  | "newPassword"
  | "mfaSetup"
  | "mfa"
  | "signedIn"
  | "forgot"
  | "reset";

interface AuthState {
  status: AuthStatus;
  email: string;
  error: string;
  notice: string;
  /** otpauth secret to show during first-time TOTP enrollment. */
  mfaSecret: string;
  signIn(email: string, password: string): void;
  completeNewPassword(newPassword: string): void;
  verifyMfaSetup(code: string): void;
  submitMfaCode(code: string): void;
  startForgot(): void;
  requestReset(email: string): void;
  confirmReset(code: string, newPassword: string): void;
  backToSignIn(): void;
  signOut(): void;
}

const Ctx = createContext<AuthState | null>(null);
export const useAuth = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside provider");
  return v;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [mfaSecret, setMfaSecret] = useState("");
  const userRef = useRef<CognitoUser | null>(null);

  // Restore an existing session on load.
  useEffect(() => {
    const pool = getPool();
    const u = pool?.getCurrentUser();
    if (!u) return setStatus("signedOut");
    u.getSession((err: Error | null, s: { isValid(): boolean } | null) => {
      setStatus(err || !s?.isValid() ? "signedOut" : "signedIn");
    });
  }, []);

  const callbacks = (): IAuthenticationCallback => ({
    onSuccess: () => { setError(""); setStatus("signedIn"); },
    onFailure: (err) => setError(err?.message ?? "Sign-in failed"),
    newPasswordRequired: () => { setError(""); setStatus("newPassword"); },
    totpRequired: () => { setError(""); setStatus("mfa"); },
    mfaRequired: () => { setError(""); setStatus("mfa"); },
    mfaSetup: () => {
      userRef.current?.associateSoftwareToken({
        associateSecretCode: (secret: string) => { setMfaSecret(secret); setStatus("mfaSetup"); },
        onFailure: (err) => setError(err?.message ?? "MFA setup failed"),
      });
    },
  });

  function signIn(em: string, password: string) {
    setError("");
    const pool = getPool();
    if (!pool) { setError("Admin sign-in isn't configured yet (missing Cognito settings)."); return; }
    const user = new CognitoUser({ Username: em, Pool: pool });
    userRef.current = user;
    setEmail(em);
    user.authenticateUser(new AuthenticationDetails({ Username: em, Password: password }), callbacks());
  }

  function completeNewPassword(newPassword: string) {
    setError("");
    userRef.current?.completeNewPasswordChallenge(newPassword, {}, callbacks());
  }

  function verifyMfaSetup(code: string) {
    setError("");
    userRef.current?.verifySoftwareToken(code, "authenticator", {
      onSuccess: () => setStatus("signedIn"),
      onFailure: (err) => setError(err?.message ?? "Invalid code"),
    });
  }

  function submitMfaCode(code: string) {
    setError("");
    userRef.current?.sendMFACode(code, callbacks(), "SOFTWARE_TOKEN_MFA");
  }

  function startForgot() {
    setError("");
    setNotice("");
    setStatus("forgot");
  }

  // Ask Cognito to email a reset code, then move to the code + new-password form.
  // Works regardless of TOTP MFA - password reset doesn't require the MFA code.
  function requestReset(em: string) {
    setError("");
    const pool = getPool();
    if (!pool) { setError("Admin sign-in isn't configured yet (missing Cognito settings)."); return; }
    const user = new CognitoUser({ Username: em, Pool: pool });
    userRef.current = user;
    setEmail(em);
    user.forgotPassword({
      onSuccess: () => setStatus("reset"),
      inputVerificationCode: () => setStatus("reset"),
      onFailure: (err) => setError(err?.message ?? "Couldn't start the reset. Check the email address."),
    });
  }

  // Confirm the emailed code + new password, then return to sign-in.
  function confirmReset(code: string, newPassword: string) {
    setError("");
    const user = userRef.current;
    if (!user) { setError("Start again from Forgot password."); setStatus("forgot"); return; }
    user.confirmPassword(code.trim(), newPassword, {
      onSuccess: () => {
        setStatus("signedOut");
        setNotice("Password changed. Sign in with your new password.");
      },
      onFailure: (err) => setError(err?.message ?? "Reset failed. Check the code and try again."),
    });
  }

  function backToSignIn() {
    setError("");
    setNotice("");
    setStatus("signedOut");
  }

  function signOut() {
    getPool()?.getCurrentUser()?.signOut();
    userRef.current = null;
    setStatus("signedOut");
  }

  return (
    <Ctx.Provider
      value={{
        status, email, error, notice, mfaSecret,
        signIn, completeNewPassword, verifyMfaSetup, submitMfaCode,
        startForgot, requestReset, confirmReset, backToSignIn,
        signOut,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}
